import type { ReactNode } from "react";
import { X } from "lucide-react";
import { cx } from "./cx";

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
  count?: number;
}

export function SegmentedFilter<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly SegmentOption<T>[];
  onChange: (value: T) => void;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className="inline-flex h-[30px] flex-none items-center gap-0.5 rounded-lg border border-border bg-bg p-0.5"
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={o.value === value}
          onClick={() => {
            onChange(o.value);
          }}
          className="h-6 whitespace-nowrap rounded-md px-[9px] text-md text-muted hover:bg-surface-2 hover:text-fg aria-pressed:bg-surface-3 aria-pressed:font-semibold aria-pressed:text-fg"
        >
          {o.label}
          {o.count === undefined ? null : <span className="ml-1 font-mono text-xs text-muted">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function FilterChip({ label, value, onRemove }: { label: string; value: ReactNode; onRemove: () => void }) {
  return (
    <span className="inline-flex h-6 items-center gap-1 whitespace-nowrap rounded-full border border-border bg-bg pr-1 pl-[9px] text-md">
      <small className="text-sm text-muted">{label}</small> {value}
      <button
        type="button"
        aria-label={`Remove ${label} filter`}
        onClick={onRemove}
        className="grid size-[18px] place-items-center rounded-full text-muted hover:bg-surface-3 hover:text-fg"
      >
        <X size={12} strokeWidth={1.8} aria-hidden="true" />
      </button>
    </span>
  );
}

export function LinkButton({
  children,
  onClick,
  className,
}: {
  children: ReactNode;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        "whitespace-nowrap rounded-md px-1.5 py-0.5 text-md font-strong text-link hover:bg-accent-soft hover:underline",
        className,
      )}
    >
      {children}
    </button>
  );
}

export function FilterGroup({
  title,
  children,
  onClear,
}: {
  title: string;
  children: ReactNode;
  onClear?: (() => void) | null;
}) {
  return (
    <fieldset className="m-0 mb-4 border-0 p-0" aria-label={title}>
      <legend className="mb-1.5 flex w-full items-baseline px-2 text-xs font-semibold tracking-[0.06em] text-muted uppercase">
        {title}
        {onClear ? (
          <button
            type="button"
            aria-label={`Clear ${title}`}
            onClick={onClear}
            className="ml-auto rounded-md px-1 font-medium tracking-normal text-link normal-case hover:underline"
          >
            Clear
          </button>
        ) : null}
      </legend>
      <div className="flex flex-col gap-px">{children}</div>
    </fieldset>
  );
}

export function FilterOption({
  type,
  name,
  checked,
  label,
  count,
  onChange,
  icon,
  disabledReason,
}: {
  type: "radio" | "checkbox";
  name: string;
  checked: boolean;
  label: string;
  count?: number | undefined;
  onChange: () => void;
  icon?: ReactNode;
  disabledReason?: string | undefined;
}) {
  return (
    <label
      title={disabledReason}
      className={cx(
        "flex h-[27px] cursor-pointer items-center gap-2 rounded-md px-2 hover:bg-surface-2",
        checked && type === "radio" && "bg-accent-soft",
        ((count === 0 && !checked) || disabledReason) && "text-muted",
        disabledReason && "cursor-default hover:bg-transparent",
      )}
    >
      <input
        type={type}
        name={name}
        checked={checked}
        disabled={disabledReason !== undefined && !checked}
        onChange={onChange}
        className="accent-[var(--color-link)]"
      />
      {icon}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {disabledReason ? <span className="text-xs">{disabledReason}</span> : null}
      {!disabledReason && count !== undefined ? <span className="font-mono text-xs text-muted">{count}</span> : null}
    </label>
  );
}
